import React from 'react';
import { Box, Typography, Button, IconButton, Select, MenuItem, TextField, Radio, RadioGroup, FormControlLabel, Checkbox, FormGroup, FormLabel } from '@mui/material';
import { useFormContext } from '../context/FormContext';
import { fileToBase64 } from '../utils/fileUtils';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    VERTICAL_OCCLUSAL_LEVEL_OPTIONS, VERTICAL_AXIS_OPTIONS,
    MESIOANGULAR_CONTACT_OPTIONS, MESIOANGULAR_DAMAGE_OPTIONS,
    DISTOANGULAR_CONTACT_OPTIONS, DISTOANGULAR_FEATURE_OPTIONS,
    TRANSVERSE_ORIENTATION_OPTIONS, TRANSVERSE_ROOTS_OPTIONS,
    PELL_GREGORY_DEPTH_OPTIONS, RAMUS_RELATION_OPTIONS
} from './ImpactionConstants';
import ImpactionTextPreview from './ImpactionTextPreview';
import { generateMandibleAdjacentText } from '../utils/impactionTextGenerator';

// Helper for consistent label styling
const FieldLabel = ({ children }) => (
    <Typography sx={{ fontSize: '13px', mb: '5px' }}>
        {children}
    </Typography>
);

const ImpactionTab_Adjacent = ({ selectedTooth }) => {
    const { state, actions } = useFormContext();
    const toothData = state.impactionData?.[selectedTooth] || {};
    const adjacentData = toothData.adjacent || {};

    const updateData = (field, value) => {
        actions.setImpactionData({
            ...state.impactionData,
            [selectedTooth]: {
                ...toothData,
                adjacent: {
                    ...adjacentData,
                    [field]: value
                }
            }
        });
    };

    const handleImageUpload = async (key) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            try {
                const base64 = await fileToBase64(file);
                const fileData = { base64, name: file.name, type: file.type };
                updateData(key, fileData);
            } catch (error) {
                console.error("Error converting file to Base64:", error);
            }
        };
        input.click();
    };

    const handleRemoveImage = (key) => {
        updateData(key, null);
    };

    const renderImageSection = (label, key, fileData, height = '250px') => {
        const preview = fileData ? fileData.base64 : null;

        return (
            <Box sx={{ width: '350px', flexShrink: 0 }}>
                <Box sx={{ position: 'relative', mb: '15px' }}>
                    <Box
                        sx={{
                            width: '350px',
                            height: height,
                            border: '2px solid black',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.200',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        {preview && (
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveImage(key)}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    bgcolor: 'background.paper',
                                    '&:hover': { bgcolor: 'grey.100' },
                                    zIndex: 1
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}

                        {preview ? (
                            <img
                                src={preview}
                                alt={label}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {label}
                            </Typography>
                        )}
                    </Box>

                    {fileData && (
                        <Typography variant="caption" sx={{ color: 'success.main', fontSize: 10, display: 'block', mt: 0.5 }}>
                            {fileData.name}
                        </Typography>
                    )}
                </Box>

                <Button
                    variant={preview ? "outlined" : "contained"}
                    fullWidth
                    color={preview ? "secondary" : "primary"}
                    onClick={() => handleImageUpload(key)}
                    sx={{ mb: '15px' }}
                >
                    {preview ? 'Replace Image' : 'Select Image'}
                </Button>
            </Box>
        );
    };

    const sectionBoxStyle = { border: '1px solid #e0e0e0', borderRadius: '4px', mb: '20px', p: '20px' };
    const sectionHeaderStyle = {
        fontSize: '18px',
        fontWeight: 'bold',
        mb: '15px',
        pb: '10px'
    };
    const inputStyle = {
        '& .MuiOutlinedInput-root': { borderRadius: 0 },
        '& .MuiOutlinedInput-input': { padding: '8px 14px', fontSize: '14px' }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom color="primary">
                Relationship with Adjacent Teeth & Ramus ({selectedTooth})
            </Typography>

            {/* SECTION: RELATION TO ADJACENT TOOTH */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>
                    RELATION TO ADJACENT TOOTH
                </Typography>
                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    {renderImageSection('Relation Image', 'relationImage', adjacentData.relationImage)}
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr', gap: '15px', alignContent: 'start' }}>

                        {/* Get Impaction Type from Position Tab Data */}
                        {(() => {
                            const impactionType = toothData.position?.type;

                            if (impactionType === 'Vertical') {
                                return (
                                    <>
                                        <Box sx={{ p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                            <FormLabel component="legend" sx={{ fontSize: '13px', fontWeight: 'bold' }}>Occlusal Plane Relation</FormLabel>
                                            <RadioGroup
                                                value={adjacentData.verticalOcclusal || ''}
                                                onChange={(e) => updateData('verticalOcclusal', e.target.value)}
                                            >
                                                {VERTICAL_OCCLUSAL_LEVEL_OPTIONS.map(opt => (
                                                    <FormControlLabel key={opt} value={opt} control={<Radio size="small" />} label={<Typography sx={{ fontSize: '13px' }}>{opt}</Typography>} />
                                                ))}
                                            </RadioGroup>
                                        </Box>
                                        <Box sx={{ p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                            <FormLabel component="legend" sx={{ fontSize: '13px', fontWeight: 'bold' }}>Long Axis Relation</FormLabel>
                                            <RadioGroup
                                                row
                                                value={adjacentData.verticalAxis || ''}
                                                onChange={(e) => updateData('verticalAxis', e.target.value)}
                                            >
                                                {VERTICAL_AXIS_OPTIONS.map(opt => (
                                                    <FormControlLabel key={opt} value={opt} control={<Radio size="small" />} label={<Typography sx={{ fontSize: '13px' }}>{opt}</Typography>} />
                                                ))}
                                            </RadioGroup>
                                        </Box>
                                    </>
                                );
                            } else if (impactionType === 'Mesioangular') {
                                return (
                                    <>
                                        <Box sx={{ p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                            <FormLabel component="legend" sx={{ fontSize: '13px', fontWeight: 'bold' }}>Contact with Distal Surface of 7</FormLabel>
                                            <RadioGroup
                                                value={adjacentData.mesioContact || ''}
                                                onChange={(e) => updateData('mesioContact', e.target.value)}
                                            >
                                                {MESIOANGULAR_CONTACT_OPTIONS.map(opt => (
                                                    <FormControlLabel key={opt} value={opt} control={<Radio size="small" />} label={<Typography sx={{ fontSize: '13px' }}>{opt}</Typography>} />
                                                ))}
                                            </RadioGroup>
                                        </Box>
                                        <Box sx={{ p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                            <FormLabel component="legend" sx={{ fontSize: '13px', fontWeight: 'bold' }}>Associated Damage/Resorption</FormLabel>
                                            <FormGroup>
                                                {MESIOANGULAR_DAMAGE_OPTIONS.map(opt => (
                                                    <FormControlLabel
                                                        key={opt}
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={adjacentData[`mesioDamage_${opt}`] || false}
                                                                onChange={(e) => updateData(`mesioDamage_${opt}`, e.target.checked)}
                                                            />
                                                        }
                                                        label={<Typography sx={{ fontSize: '13px' }}>Causing {opt}</Typography>}
                                                    />
                                                ))}
                                            </FormGroup>
                                        </Box>
                                    </>
                                );
                            } else if (impactionType === 'Distoangular') {
                                return (
                                    <>
                                        <Box sx={{ p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                            <FormLabel component="legend" sx={{ fontSize: '13px', fontWeight: 'bold' }}>Ramus Relationship</FormLabel>
                                            <RadioGroup
                                                value={adjacentData.distoContact || ''}
                                                onChange={(e) => updateData('distoContact', e.target.value)}
                                            >
                                                {DISTOANGULAR_CONTACT_OPTIONS.map(opt => (
                                                    <FormControlLabel key={opt} value={opt} control={<Radio size="small" />} label={<Typography sx={{ fontSize: '13px' }}>{opt}</Typography>} />
                                                ))}
                                            </RadioGroup>
                                        </Box>
                                        <Box sx={{ p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                            <FormGroup>
                                                {DISTOANGULAR_FEATURE_OPTIONS.map(opt => (
                                                    <FormControlLabel
                                                        key={opt}
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={adjacentData[`distoFeature_${opt}`] || false}
                                                                onChange={(e) => updateData(`distoFeature_${opt}`, e.target.checked)}
                                                            />
                                                        }
                                                        label={<Typography sx={{ fontSize: '13px' }}>{opt}</Typography>}
                                                    />
                                                ))}
                                            </FormGroup>
                                        </Box>
                                    </>
                                );
                            } else if (impactionType === 'Transverse') {
                                return (
                                    <>
                                        <Box sx={{ p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                            <FormLabel component="legend" sx={{ fontSize: '13px', fontWeight: 'bold' }}>Crown Orientation</FormLabel>
                                            <RadioGroup
                                                row
                                                value={adjacentData.transverseCrown || ''}
                                                onChange={(e) => updateData('transverseCrown', e.target.value)}
                                            >
                                                {TRANSVERSE_ORIENTATION_OPTIONS.map(opt => (
                                                    <FormControlLabel key={opt} value={opt} control={<Radio size="small" />} label={<Typography sx={{ fontSize: '13px' }}>{opt}</Typography>} />
                                                ))}
                                            </RadioGroup>
                                        </Box>
                                        <Box sx={{ p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                            <FormLabel component="legend" sx={{ fontSize: '13px', fontWeight: 'bold' }}>Roots Direction</FormLabel>
                                            <RadioGroup
                                                row
                                                value={adjacentData.transverseRoots || ''}
                                                onChange={(e) => updateData('transverseRoots', e.target.value)}
                                            >
                                                {TRANSVERSE_ROOTS_OPTIONS.map(opt => (
                                                    <FormControlLabel key={opt} value={opt} control={<Radio size="small" />} label={<Typography sx={{ fontSize: '13px' }}>{opt}</Typography>} />
                                                ))}
                                            </RadioGroup>
                                        </Box>
                                    </>
                                );
                            } else {
                                // Default / Fallback for Horizontal, Inverted, Ectopic or unselected
                                return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <FieldLabel>Specific Relations / Notes</FieldLabel>
                                        <TextField
                                            multiline
                                            rows={2}
                                            placeholder={impactionType ? `Enter specific relations for ${impactionType} impaction` : "Select Impaction Type in Position Tab first"}
                                            value={adjacentData.otherRelation || ''}
                                            onChange={(e) => updateData('otherRelation', e.target.value)}
                                            sx={inputStyle}
                                        />
                                    </Box>
                                );
                            }
                        })()}

                    </Box>
                </Box>
            </Box>

            {/* SECTION: RELATION & RAMUS */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>
                    RELATION & RAMUS
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <FieldLabel>Pell & Gregory Depth</FieldLabel>
                        <Select
                            value={adjacentData.pellDepth || ''}
                            onChange={(e) => updateData('pellDepth', e.target.value)}
                            displayEmpty
                            size="small"
                            sx={inputStyle}
                        >
                            {PELL_GREGORY_DEPTH_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <FieldLabel>Relation to Ramus (Space)</FieldLabel>
                        <Select
                            value={adjacentData.ramusRelation || ''}
                            onChange={(e) => updateData('ramusRelation', e.target.value)}
                            displayEmpty
                            size="small"
                            sx={inputStyle}
                        >
                            {RAMUS_RELATION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                    </Box>
                </Box>
            </Box>

            <ImpactionTextPreview text={generateMandibleAdjacentText(adjacentData, toothData.position?.type)} />

        </Box>
    );
};

export default ImpactionTab_Adjacent;
